import { useState, useEffect } from "react";
import { useAuth, useAuthActions } from "@/lib/auth/useAuth";
import {
  useNavigate,
  useSearchParams,
  useLocation,
  Link,
} from "react-router-dom";
import { billingService } from "@/services/billingService";
import { useAuthStore } from "@/state/authStore";
import { PLANS, PLAN_DISPLAY, TRIAL_DAYS } from "@/config/plans";
const MONTHLY_PRICE_ID = import.meta.env.VITE_STRIPE_MONTHLY_PRICE_ID as
  | string
  | undefined;

// ─── helpers ──────────────────────────────────────────────────────────────────

function trialDaysRemaining(
  currentPeriodEnd: string | undefined,
): number | null {
  if (!currentPeriodEnd) return null;
  const endMs = new Date(currentPeriodEnd).getTime();
  if (Number.isNaN(endMs)) return null;
  return Math.max(0, Math.ceil((endMs - Date.now()) / (1000 * 60 * 60 * 24)));
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

// ─── Component ────────────────────────────────────────────────────────────────

export function BillingPage() {
  const { subscription } = useAuth();
  const { refreshSubscription } = useAuthActions();
  const account = useAuthStore((s) => s.account);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const pendingSubscription = (
    location.state as { pendingSubscription?: boolean } | null
  )?.pendingSubscription;

  const hasActiveSubscription =
    subscription?.status === "active" || subscription?.status === "trialing";
  const isTrialing = subscription?.status === "trialing";

  const [loadingMonthly, setLoadingMonthly] = useState(false);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const daysLeft = isTrialing
    ? trialDaysRemaining(subscription?.currentPeriodEnd)
    : null;
  const trialProgress =
    daysLeft !== null
      ? Math.round(((TRIAL_DAYS - daysLeft) / TRIAL_DAYS) * 100)
      : 0;
  const isExpiringSoon = daysLeft !== null && daysLeft <= 2;

  // Auto-poll when landing from Stripe checkout
  useEffect(() => {
    if (!pendingSubscription || !isAuthenticated || hasActiveSubscription)
      return;

    let attempts = 0;
    const maxAttempts = 10;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const poll = async () => {
      if (cancelled) return;
      attempts++;
      const active = await refreshSubscription();
      if (cancelled) return; // component unmounted during the await
      if (active) {
        navigate("/dashboard");
        return;
      }
      if (attempts < maxAttempts) {
        timer = setTimeout(() => void poll(), 3000);
      } else {
        setPollTimedOut(true);
      }
    };

    timer = setTimeout(() => void poll(), 3000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [pendingSubscription, isAuthenticated, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isAuthenticated || hasActiveSubscription) return;
    const plan = searchParams.get("plan");
    if (plan) navigate(`/signup?plan=${plan}`);
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSubscribe(priceId: string | undefined) {
    if (!isAuthenticated) {
      navigate("/signup?plan=monthly");
      return;
    }
    if (!priceId) {
      setError("Stripe price ID is not configured. Please contact support.");
      return;
    }
    if (!account?.email || !account?.id) {
      setError("You must be logged in to subscribe.");
      return;
    }

    setError(null);
    setLoadingMonthly(true);
    try {
      const url = await billingService.createCheckoutSession(
        priceId,
        account.email,
        account.id,
      );
      window.location.href = url;
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to start checkout. Please try again.",
      );
      setLoadingMonthly(false);
    }
  }

  async function handleRetryDashboard() {
    setRetrying(true);
    try {
      const active = await Promise.race([
        refreshSubscription(),
        new Promise<boolean>((resolve) => setTimeout(() => resolve(false), 15_000)),
      ]);
      if (active) {
        navigate("/dashboard");
      } else {
        setError("Subscription not active yet. Please wait a moment and try again.");
      }
    } catch (err) {
      console.error("[BillingPage] retry failed:", err);
      setError("Could not verify subscription. Please try again.");
    } finally {
      setRetrying(false);
    }
  }

  async function handleManageBilling() {
    const customerId = subscription?.providerCustomerId;
    if (!customerId) return;
    setLoadingPortal(true);
    setError(null);
    try {
      const url = await billingService.createPortalSession(customerId);
      window.location.href = url;
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to open billing portal. Please try again.",
      );
      setLoadingPortal(false);
    }
  }

  // ── Processing screen (post-checkout webhook wait) ──────────────────────────
  if (pendingSubscription && isAuthenticated && !hasActiveSubscription) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-6 py-12">
        <div className="text-center max-w-md">
          <div aria-live="polite" aria-atomic="true">
          {pollTimedOut ? (
            <>
              <div className="w-16 h-16 bg-zinc-800/60 border border-zinc-700/50 rounded-xl flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-8 h-8 text-[#8a4545]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">
                Subscription Not Confirmed Yet
              </h2>
              <p className="text-zinc-400 mb-2">
                Your payment was received but the subscription hasn't activated.
                This may be a webhook configuration issue.
              </p>
              <p className="text-zinc-400 text-sm mb-8">
                Check that{" "}
                <span className="text-zinc-300 font-mono">
                  STRIPE_WEBHOOK_SECRET
                </span>{" "}
                and{" "}
                <span className="text-zinc-300 font-mono">
                  SUPABASE_SERVICE_ROLE_KEY
                </span>{" "}
                are set correctly.
              </p>
            </>
          ) : (
            <>
              <div aria-hidden="true" className="w-10 h-10 border-2 border-zinc-700 border-t-[rgb(var(--cp))] rounded-full animate-spin mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-white mb-3">
                Activating Your Trial
              </h2>
              <p className="text-zinc-400 mb-8">
                Your free trial is being set up — this takes just a few seconds.
              </p>
            </>
          )}
          </div>
          <button
            onClick={() => void handleRetryDashboard()}
            disabled={retrying}
            className="w-full px-6 py-4 bg-gradient-to-b from-[rgb(var(--cp))] to-[rgb(var(--cp)/0.85)] hover:from-[rgb(var(--cs))] hover:to-[rgb(var(--cs)/0.85)] text-white font-bold rounded-lg transition-all disabled:opacity-50 shadow-lg shadow-[rgb(var(--cp)/0.25)]"
          >
            {retrying ? "Checking…" : "Check Again"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 px-4 sm:px-6 py-8 sm:py-12">
      <div className="max-w-xl mx-auto">
        {/* Error */}
        {error && (
          <div className="mb-6 px-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-sm text-center text-[#8a4545]">
            {error}{" "}
            <a
              href="mailto:support@momentumpeaks.com"
              className="underline hover:text-red-200"
            >
              support@momentumpeaks.com
            </a>
          </div>
        )}

        {/* ── Active subscription ── */}
        {hasActiveSubscription ? (
          <div className="space-y-4">
            {/* Trial status card */}
            {isTrialing && (
              <div
                className="rounded-2xl bg-zinc-900 border border-zinc-800 p-4 sm:p-6"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div
                      className={`text-xs font-bold tracking-widest uppercase mb-1 ${isExpiringSoon ? "text-[#8a4545]" : "text-[#7a6020]"}`}
                    >
                      Free Trial Active
                    </div>
                    <div className="text-2xl font-bold text-white">
                      {daysLeft !== null
                        ? daysLeft === 0
                          ? "Trial ends today"
                          : `${daysLeft} day${daysLeft === 1 ? "" : "s"} remaining`
                        : "Trial active"}
                    </div>
                    {subscription?.currentPeriodEnd && (
                      <div className="text-zinc-400 text-sm mt-1">
                        {isExpiringSoon
                          ? "Add a payment method to keep your access"
                          : `Your card will be charged ${PLAN_DISPLAY.monthly}/mo on ${formatDate(subscription.currentPeriodEnd ?? "")}`}
                      </div>
                    )}
                  </div>
                  <div
                    className={`text-3xl sm:text-4xl font-black tabular-nums ${isExpiringSoon ? "text-[#8a4545]" : "text-[#7a6020]"}`}
                  >
                    {daysLeft !== null ? daysLeft : "–"}
                  </div>
                </div>

                {/* Progress bar */}
                {daysLeft !== null && (
                  <div className="mb-4">
                    <div className="flex justify-between text-xs text-zinc-400 mb-1.5">
                      <span>Day 1</span>
                      <span>Day {TRIAL_DAYS}</span>
                    </div>
                    <div
                      className="h-2 bg-zinc-800 rounded-full overflow-hidden"
                      role="progressbar"
                      aria-valuenow={trialProgress}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-label={`Trial ${trialProgress}% used`}
                    >
                      <div
                        className={`h-full rounded-full transition-all ${isExpiringSoon ? "bg-[#c09090]" : "bg-[#c8aa5e]"}`}
                        style={{ width: `${trialProgress}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs mt-1.5">
                      <span className="text-zinc-400">Trial started</span>
                      <span
                        className={
                          isExpiringSoon
                            ? "font-semibold text-[#8a4545]"
                            : "text-zinc-400"
                        }
                      >
                        {trialProgress}% used
                      </span>
                    </div>
                  </div>
                )}

                {/* CTA when expiring soon */}
                {isExpiringSoon && subscription?.providerCustomerId && (
                  <button
                    onClick={() => void handleManageBilling()}
                    disabled={loadingPortal}
                    className="w-full py-3 bg-red-600 hover:bg-red-500 text-white font-bold rounded-xl transition-all disabled:opacity-50 text-sm"
                  >
                    {loadingPortal
                      ? "Opening…"
                      : "Add Payment Method — Keep Access"}
                  </button>
                )}
              </div>
            )}

            {/* Main subscription card */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4 sm:p-6">
              <div className="flex items-center gap-4 mb-5">
                <div className="w-12 h-12 bg-zinc-800/60 border border-zinc-700/50 rounded-xl flex items-center justify-center shrink-0">
                  <svg
                    className={"w-6 h-6 text-[#3c6862]"}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <div>
                  <div className="font-bold text-white">
                    {isTrialing ? "Free Trial" : "Momentum Peaks"} ·{" "}
                    {PLAN_DISPLAY.monthly}/mo
                  </div>
                  <div className="text-sm text-zinc-400 capitalize mt-0.5">
                    {subscription?.status === "trialing"
                      ? "Trialing — full access unlocked"
                      : `Status: ${subscription?.status}`}
                  </div>
                </div>
              </div>

              {/* What's included */}
              <div className="grid grid-cols-2 gap-2 mb-5">
                {PLANS.monthly.features.map((f) => (
                  <div
                    key={f}
                    className="flex items-start gap-2 text-xs text-zinc-400"
                  >
                    <svg
                      className={"w-3.5 h-3.5 shrink-0 mt-0.5 text-[#3c6862]"}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="3"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    {f}
                  </div>
                ))}
              </div>

              <div className="flex flex-col gap-3 sm:flex-row">
                <button
                  onClick={() => navigate("/dashboard")}
                  className="flex-1 py-3 bg-gradient-to-b from-[rgb(var(--cp))] to-[rgb(var(--cp)/0.85)] hover:from-[rgb(var(--cs))] hover:to-[rgb(var(--cs)/0.85)] text-white font-bold rounded-xl transition-all text-sm shadow-lg shadow-[rgb(var(--cp)/0.25)]"
                >
                  Go to Dashboard
                </button>
                {subscription?.providerCustomerId && (
                  <button
                    onClick={() => void handleManageBilling()}
                    disabled={loadingPortal}
                    className="px-5 py-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-medium rounded-xl transition-all disabled:opacity-50 text-sm"
                  >
                    {loadingPortal ? "Opening…" : "Manage"}
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          /* ── No subscription — pricing card ── */
          <div>
            <div className="text-center mb-8">
              <div className={"inline-flex items-center gap-2 text-xs font-bold px-3 py-1.5 rounded-full mb-4 tracking-wide bg-[#C49A3C]/10 border border-[#C49A3C] text-[#8B6B2A]"}>
                <svg
                  className="w-3.5 h-3.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                {TRIAL_DAYS}-Day Free Trial — No Commitment
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">
                Start Training Smarter
              </h1>
              <p className="text-zinc-400">
                Full access, free for {TRIAL_DAYS} days. Your card won't be
                charged until your trial ends.
              </p>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
              {/* Price header */}
              <div className="px-8 pt-8 pb-6 border-b border-zinc-800">
                <div className="text-zinc-400 text-sm font-medium mb-1">
                  {PLANS.monthly.label}
                </div>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-5xl font-black text-white">
                    {PLAN_DISPLAY.monthly}
                  </span>
                  <span className="text-zinc-400 text-lg">/ month</span>
                </div>
                <p className="text-zinc-400 text-sm">
                  after your {TRIAL_DAYS}-day free trial
                </p>
              </div>

              {/* Features */}
              <div className="px-8 py-6 space-y-3">
                {PLANS.monthly.features.map((feature) => (
                  <div key={feature} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-zinc-800/60 flex items-center justify-center shrink-0">
                      <svg
                        className={"w-3 h-3 text-[#3c6862]"}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="3"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <span className="text-zinc-300 text-sm">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="px-8 pb-8">
                <button
                  onClick={() => void handleSubscribe(MONTHLY_PRICE_ID)}
                  disabled={loadingMonthly}
                  className="w-full py-4 bg-gradient-to-b from-[rgb(var(--cp))] to-[rgb(var(--cp)/0.85)] hover:from-[rgb(var(--cs))] hover:to-[rgb(var(--cs)/0.85)] text-white font-bold rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2 text-base shadow-lg shadow-[rgb(var(--cp)/0.25)]"
                >
                  {loadingMonthly ? (
                    <>
                      <svg
                        className="w-4 h-4 animate-spin"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                      Redirecting…
                    </>
                  ) : (
                    `Start ${TRIAL_DAYS}-Day Free Trial`
                  )}
                </button>

                {/* Trust signals */}
                <div className="mt-4 flex items-center justify-center gap-4 text-xs text-zinc-400">
                  <span className="flex items-center gap-1">
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                    Secure checkout
                  </span>
                  <span>·</span>
                  <span>Cancel anytime</span>
                  <span>·</span>
                  <span>No charge today</span>
                </div>
              </div>
            </div>

            {!isAuthenticated && (
              <p className="mt-6 text-center text-sm text-zinc-400">
                Already have an account?{" "}
                <Link
                  to="/login"
                  className="text-[rgb(var(--cs))] hover:underline font-medium"
                >
                  Log in
                </Link>
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
